import { type ReactElement, useEffect, useState } from 'react';
import { type AnalyticsExportFormat, type AnalyticsExportSchedule, type AnalyticsExportSource } from '@vello/api-client';
import { Button, CheckRow, Field, Notice, Select, TextInput, FormStack, Stack, Ink, Text, Row } from '@vello/web-theme';
import { type ScheduleDraft, cadenceOfDraft, checkDraft, draftOfSchedule, emptyDraft, exportErrorView, toggleRecipient } from '../../../src/index';
import { useAdmin } from '../../session';
import { useConsole } from '../../console';
import { useT } from '../../i18n/provider';
import { useVocab } from '../../i18n/vocab';
import { FormDialog } from '../form-dialog';
import { PersonName } from '../person-name';
import { useBiCatalog } from './bi-hooks';
import { useBiText } from './bi-ui';
import { useExportRecipients, useExportTemplates } from './bi-exports-store';
import { useSavedReports } from './bi-reports-store';

export interface SchedulePreset {
  readonly source?: AnalyticsExportSource;
  readonly templateId?: string;
  readonly columns: readonly string[];
  readonly format: AnalyticsExportFormat;
  readonly delimiter: ',' | ';';
}

export interface ScheduleDialogProps {
  readonly open: boolean;
  /** What to export, already decided (from an export dialog). Without it the dialog asks for a saved report or a template. */
  readonly preset?: SchedulePreset;
  /** Edit this schedule (the name, the time, the zone, the recipients and the on/off switch; what it exports is kept). */
  readonly schedule?: AnalyticsExportSchedule;
  readonly onClose: () => void;
  readonly onSaved: (schedule: AnalyticsExportSchedule) => void;
}

/**
 * "Agendar exportação" (D532): a name, a cadence at a LOCAL time in the zone of a market of the operator's reach, and the OPERATORS who receive a
 * link by e-mail. Recipients are chosen from a list of operators, never typed; the server refuses one who is not an active operator or who cannot read
 * what the schedule reads, and asks again at every run. A refusal stays in the dialog, in plain words.
 */
export function ScheduleDialog({ open, preset, schedule, onClose, onSaved }: ScheduleDialogProps): ReactElement | null {
  const { t } = useT();
  const { v } = useVocab();
  const text = useBiText();
  const { runtime, me } = useAdmin();
  const { toast } = useConsole();
  const catalog = useBiCatalog();
  const markets = catalog.state.status === 'ready' ? catalog.state.value.markets : [];
  const recipients = useExportRecipients(open);
  const picking = preset === undefined && schedule === undefined;
  const reports = useSavedReports();
  const templates = useExportTemplates(open && picking);
  const [draft, setDraft] = useState<ScheduleDraft>(() => emptyDraft(''));
  const [kind, setKind] = useState<'report' | 'template'>('report');
  const [pick, setPick] = useState('');
  const [problem, setProblem] = useState<string | undefined>(undefined);
  const [field, setField] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(schedule !== undefined ? draftOfSchedule(schedule) : emptyDraft(markets[0]?.id ?? ''));
    setProblem(undefined);
    setField(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the form starts when it opens (and once the markets are known)
  }, [open, schedule, markets.length]);

  const set = <K extends keyof ScheduleDraft>(key: K, value: ScheduleDraft[K]): void => setDraft((d) => ({ ...d, [key]: value }));

  const submit = async (): Promise<void> => {
    setProblem(undefined);
    setField(undefined);
    const checked = checkDraft(draft);
    if (!checked.ok) {
      setProblem(t(checked.messageId));
      setField(checked.field);
      return;
    }
    const cadence = cadenceOfDraft(draft);
    if (cadence === undefined) {
      setProblem(t('bi.exp.err.cadence'));
      return;
    }
    if (schedule === undefined && preset === undefined && pick === '') {
      setProblem(t('bi.exp.sch.source.none'));
      return;
    }
    setBusy(true);
    const common = { name: draft.name.trim(), cadence, zoneMarket: draft.zoneMarket, recipients: draft.recipients, enabled: draft.enabled };
    const res =
      schedule !== undefined
        ? await runtime.analyticsBi.updateExportSchedule(schedule.id, common)
        : await runtime.analyticsBi.saveExportSchedule({
            ...common,
            ...(preset !== undefined
              ? { ...(preset.source !== undefined ? { source: preset.source } : {}), ...(preset.templateId !== undefined ? { templateId: preset.templateId } : {}), columns: preset.columns, format: preset.format, ...(preset.format === 'CSV' ? { delimiter: preset.delimiter } : {}) }
              : kind === 'report'
                ? { source: { kind: 'SAVED_REPORT' as const, reportId: pick } }
                : { templateId: pick }),
          });
    setBusy(false);
    if (!res.ok) {
      setProblem(t(exportErrorView(res.error).messageId));
      return;
    }
    toast({ tone: 'success', message: t('bi.exp.sch.saved', { name: res.value.schedule.name }) });
    onSaved(res.value.schedule);
  };

  const days = Array.from({ length: 31 }, (_, i) => i + 1);
  const savedReports = reports.state.status === 'ready' ? reports.state.value : [];
  const templateList = templates.state.status === 'ready' ? templates.state.value : [];
  const people = recipients.status === 'ready' ? recipients.value : [];

  return (
    <FormDialog open={open} id="bi-schedule" icon="history" title={schedule !== undefined ? t('bi.exp.sch.editTitle') : t('bi.exp.sch.title')} body={t('bi.exp.sch.body')} busy={busy} initialFocusId="bi-schedule-name" onCancel={onClose}>
      <FormStack gap={4}
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
        noValidate
      >
        <Field label={t('bi.exp.sch.name')} htmlFor="bi-schedule-name" {...(field === 'name' && problem !== undefined ? { error: problem } : {})}>
          <TextInput id="bi-schedule-name" value={draft.name} maxLength={80} onChange={(e) => set('name', e.target.value)} />
        </Field>

        {picking && (
          <Stack gap={2}>
            <Field label={t('bi.exp.sch.source')} htmlFor="bi-schedule-kind">
              <Select id="bi-schedule-kind" value={kind} onChange={(e) => { setKind(e.target.value === 'template' ? 'template' : 'report'); setPick(''); }}>
                <option value="report">{t('bi.exp.sch.source.report')}</option>
                <option value="template">{t('bi.exp.sch.source.template')}</option>
              </Select>
            </Field>
            <Field label={kind === 'report' ? t('bi.exp.sch.source.report') : t('bi.exp.sch.source.template')} htmlFor="bi-schedule-pick">
              <Select id="bi-schedule-pick" value={pick} onChange={(e) => setPick(e.target.value)}>
                <option value="">{t('bi.exp.sch.source.choose')}</option>
                {kind === 'report' ? savedReports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>) : templateList.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </Select>
            </Field>
          </Stack>
        )}

        <Field label={t('bi.exp.sch.freq')} htmlFor="bi-schedule-kind-cadence">
          <Select id="bi-schedule-kind-cadence" value={draft.kind} onChange={(e) => set('kind', e.target.value === 'DAILY' ? 'DAILY' : e.target.value === 'MONTHLY' ? 'MONTHLY' : 'WEEKLY')}>
            <option value="DAILY">{v('analytics.export_frequency', 'DAILY')}</option>
            <option value="WEEKLY">{v('analytics.export_frequency', 'WEEKLY')}</option>
            <option value="MONTHLY">{v('analytics.export_frequency', 'MONTHLY')}</option>
          </Select>
        </Field>
        {draft.kind === 'WEEKLY' && (
          <Field label={t('bi.exp.sch.weekday')} htmlFor="bi-schedule-weekday">
            <Select id="bi-schedule-weekday" value={String(draft.weekday)} onChange={(e) => set('weekday', Number(e.target.value))}>
              {[1, 2, 3, 4, 5, 6, 7].map((d) => <option key={d} value={d}>{t(`bi.exp.wdName.${d}`)}</option>)}
            </Select>
          </Field>
        )}
        {draft.kind === 'MONTHLY' && (
          <Field label={t('bi.exp.sch.day')} htmlFor="bi-schedule-day" {...(field === 'day' && problem !== undefined ? { error: problem } : {})}>
            <Select id="bi-schedule-day" value={String(draft.day)} onChange={(e) => set('day', Number(e.target.value))}>
              {days.map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
          </Field>
        )}
        <Field label={t('bi.exp.sch.time')} htmlFor="bi-schedule-time" {...(field === 'time' && problem !== undefined ? { error: problem } : {})}>
          <TextInput id="bi-schedule-time" type="time" value={draft.time} onChange={(e) => set('time', e.target.value)} />
        </Field>
        <Field label={t('bi.exp.sch.zone')} htmlFor="bi-schedule-zone" hint={t('bi.exp.sch.zone.hint')} {...(field === 'zone' && problem !== undefined ? { error: problem } : {})}>
          <Select id="bi-schedule-zone" value={draft.zoneMarket} onChange={(e) => set('zoneMarket', e.target.value)}>
            {markets.map((m) => <option key={m.id} value={m.id}>{`${m.name} · ${m.timezone}`}</option>)}
          </Select>
        </Field>

        <Stack as="fieldset" gap={1} aria-describedby="bi-schedule-recipients-hint">
          <Ink as="legend" strong>{t('bi.exp.sch.recipients')}</Ink>
          <Text role="caption" tone="secondary" id="bi-schedule-recipients-hint">{t('bi.exp.sch.recipients.hint')}</Text>
          {recipients.status === 'loading' && <Ink tone="secondary">{t('ui.loading')}</Ink>}
          {recipients.status === 'error' && <Notice tone="warning">{t(recipients.error.messageId)}</Notice>}
          {recipients.status === 'ready' && people.length === 0 && <Ink tone="secondary">{t('bi.exp.sch.recipients.empty')}</Ink>}
          {people.map((p) => (
            <CheckRow key={p.userId} id={`bi-schedule-r-${p.userId}`} checked={draft.recipients.includes(p.userId)} onChange={(on) => set('recipients', toggleRecipient(draft.recipients, p.userId, on))}>
              {p.displayName ?? (p.userId === me?.id ? text.t('bi.exp.tpl.mine') : <PersonName id={p.userId} />)}
            </CheckRow>
          ))}
          {field === 'recipients' && problem !== undefined && <Notice tone="warning">{problem}</Notice>}
        </Stack>

        <CheckRow id="bi-schedule-enabled" checked={draft.enabled} onChange={(on) => set('enabled', on)}>
          {t('bi.exp.sch.enabled')}
        </CheckRow>

        {problem !== undefined && field === undefined && <Notice tone="warning">{problem}</Notice>}
        <Row gap={2} align="stretch">
          <Button className="vello-admin-a-btn-grow" variant="ghost" onClick={onClose}>
            {t('ui.cancel')}
          </Button>
          <Button className="vello-admin-a-btn-grow" type="submit" variant="primary" loading={busy}>
            {t('bi.exp.sch.save')}
          </Button>
        </Row>
      </FormStack>
    </FormDialog>
  );
}
